import React, { Suspense, useEffect } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { BrowserRouter, Route, Routes } from 'react-router-dom'
import { actions } from 'slices/app.slice'
import Fallback from 'components/Fallback'
import AppErrorBoundary from 'components/AppErrorBoundary/AppErrorBoundary'
import lazyWithRetry from 'utils/lazyWithRetry'
import styles from 'index.module.scss'
import { Image } from 'react-bootstrap'
import { rememberLandingParams, storedLandingParams } from 'utils/metaEvents'
import { images } from './theme'
import { database } from './utils/firebase'
import { detectVariant, storeVariant } from './utils/variant'

const Auth = lazyWithRetry(() => import('./pages/auth'))
const Dashboard = lazyWithRetry(() => import('./pages/dashboard'))
const CompletePayment = lazyWithRetry(() => import('./pages/CompletePayment'))

function Router(props) {
  const dispatch = useDispatch()
  const { checked, loggedIn } = useSelector((state) => state.app)

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search || props.urlParams)

    let utmParams = {};
    urlParams.forEach((value, key) => {
      if (key !== 'source' && key !== 'environment' && key !== 'user_id' && key !== 'medical_issue' && key !== 'ctx') {
        utmParams[key] = value;
      }
    });

    if (Object.keys(utmParams).length <= 0) {
      utmParams = null;
    }
    // Care: keep the tab's first landing parameters across reloads, and restore them when a reloaded
    // URL has none, so they still reach users/{uid}/utmParams at sign-up (utils/metaEvents.js).
    rememberLandingParams(utmParams)
    if (!utmParams) utmParams = storedLandingParams()
    const key = (utmParams && (utmParams.gclid || utmParams.gbraid || utmParams.wbraid || utmParams.gclsrc)) || null;
    const variant = detectVariant(window.location.pathname)
    if (variant) {
      storeVariant(variant)
      dispatch(actions.setVariant(variant))
    }
    const source = urlParams.get('source') || null
      const environment = urlParams.get('environment') || null
      const user_id = urlParams.get('user_id') || null
      const medicalIssue = (urlParams.get('medical_issue') || '').trim()
      if (medicalIssue) {
        try { sessionStorage.setItem('ydo_medical_issue', medicalIssue) } catch (e) {}
      }
      const ctxToken = (urlParams.get('ctx') || '').trim()
      if (ctxToken && ctxToken.length <= 256) {
        // 10s timeout so a slow / unreachable handoff endpoint can't
        // sit in the request queue for the browser's default several
        // minutes. Generous enough to absorb Cloud Function cold
        // starts (1-3s) on top of a slow mobile connection. The
        // prefill is best-effort — on timeout we abort and the
        // funnel proceeds without it.
        //
        // Endpoint base URL is env-driven so staging and prod can
        // point at different patient-MCP deployments. Defaults to
        // staging if the env var is missing -- safer for CI / raw
        // react-scripts invocations than defaulting to prod.
        const handoffBase = process.env.REACT_APP_HANDOFF_BASE_URL || 'https://stg-connect.yourdoctors.online'
        const ctrl = new AbortController()
        const timeoutId = setTimeout(() => ctrl.abort(), 10000)
        fetch(`${handoffBase}/api/handoff/${encodeURIComponent(ctxToken)}`, { signal: ctrl.signal })
          .then((res) => (res.ok ? res.json() : null))
          .then((data) => {
            if (!data) return
            const issue = typeof data.medical_issue === 'string' ? data.medical_issue.trim() : ''
            if (issue) {
              try { sessionStorage.setItem('ydo_medical_issue', issue) } catch (e) {}
            }
            // Stash the handoff-token mint time for funnel attribution.
            // setSocialInfo picks this up at signup and writes it to
            // users/{uid}/mcpHandoffAt in RTDB. Epoch milliseconds
            // (the patient-MCP team's contract — not seconds).
            if (typeof data.created_at === 'number' && Number.isFinite(data.created_at)) {
              try { sessionStorage.setItem('ydo_handoff_created_at', String(data.created_at)) } catch (e) {}
            }
            // Backend source fallback: if the URL didn't include
            // ?source=, fall back to the source from the handoff
            // response. Defensive against tool calls that build the
            // URL without the source param.
            //
            // Coverage: updates state.app.source, so downstream
            // useSelector consumers (Signup, downloadApp) see the
            // value. The signup-path RTDB attribution in
            // setSocialInfo (app.slice.js:167,206) reads
            // getState().app.source fresh and DOES pick up the
            // fallback — users/{uid}/sources/{source} lands correctly
            // when the user completes signup via social auth.
            //
            // Limitation: the older RTDB write inside authenticate's
            // onAuthStateChanged callback (app.slice.js:429,435)
            // captures the `source` argument at call time, so a
            // reload after auth without re-signup will miss the
            // fallback for that specific write. Spec says LLM
            // tool-call URLs always carry ?source=, so this gap is
            // moot for compliant tool callers.
            if (!source && typeof data.source === 'string' && data.source) {
              dispatch(actions.setSource(data.source))
            }
          })
          .catch((e) => { console.log('ctx handoff error', e) })
          .finally(() => clearTimeout(timeoutId))
      }
    dispatch(actions.authenticate(source, utmParams, {environment, user_id}))
    try{
      if (key){
        let updates = {};
        if (source){
          updates[`googleAds/${key}/source`] = source;
        }
        const utmKeys = Object.keys(utmParams);
        utmKeys.forEach((utmKey) => {
          updates[`googleAds/${key}/${utmKey}`] = utmParams[utmKey];
        });
        updates[`googleAds/${key}/updated_at`] = Date.now();
        if (Object.keys(updates).length > 0){
            database.ref().update(updates).then(data => {console.log('googleAds updated!');
            }).catch((error) => {
                console.log('googleAds error', error?.data || error);
            });
        }
      }
    }
    catch (e){
      console.log("error in uploadUsersUpdate", e)
    }
  }, [])

  var options = {
    enableHighAccuracy: true,
    timeout: 5000,
    maximumAge: 0,
  };
  function success(pos) {
    const crd = pos.coords;
    dispatch(actions.setLocationInfo(crd ? { latitude:crd.latitude, longitude: crd.longitude, accuracy: crd.accuracy } : null))

    console.log("Your current position is:", crd);
    console.log(`Latitude : ${crd.latitude}`);
    console.log(`Longitude: ${crd.longitude}`);
    console.log(`More or less ${crd.accuracy} meters.`);
  }

  function errors(err) {
    console.warn(`ERROR(${err.code}): ${err.message}`);
  }

  useEffect(() => {

    if (navigator && navigator.geolocation) {
      if (navigator.permissions){
        navigator.permissions
            .query({ name: "geolocation" })
            .then(function (result) {
              console.log(result);
              if (result.state === "granted") {
                //If granted then you can directly call your function here
                navigator.geolocation.getCurrentPosition(success, errors, options);
              } else if (result.state === "prompt") {
                //If prompt then the user will be asked to give permission
                navigator.geolocation.getCurrentPosition(success, errors, options);
              } else if (result.state === "denied") {
                //If denied then you have to show instructions to enable location
              }
            });
      }

    } else {
      console.log("Geolocation is not supported by this browser.");
    }
  }, [])

  if (!checked) {
    return (
      <div className={styles.background}>
        <div className={styles.logo}>

          <Image
            src={images.ydoLogoWhite}
            alt="notification"
            priority
          />
          <span className={styles.text}>Your Doctors Online</span>
        </div>
      </div>
    )
  }

  return (
    <div>
      <BrowserRouter>
        <AppErrorBoundary>
        <Suspense fallback={<Fallback />}>
          {!loggedIn ? (
            <Routes>
              <Route path="/complete-payment" element={<CompletePayment />} />
              <Route path="/auth/*" element={<Auth />} />
              <Route path="*" element={<Auth />} />
            </Routes>
          ) : (
            <Routes>
              <Route path="/complete-payment" element={<CompletePayment />} />
              <Route path={"*"} element={<Dashboard />} />
            </Routes>
          )}
        </Suspense>
        </AppErrorBoundary>
      </BrowserRouter>
    </div>
  )
}

export default Router
